<template>
  <div class="card auth-card">
    <h2 v-tx="'LoginTitle'">{{ title }}</h2>
    <p class="muted" v-tx="'LoginFormSubtitle'">{{ subtitle }}</p>

    <form @submit.prevent="onSubmit">
      <label class="field">
        <span v-tx="'LoginEmail'">{{ tx('LoginEmail') }}</span>
        <input type="email" v-model="email" required />
      </label>
      <label class="field">
        <span v-tx="'LoginPassword'">{{ tx('LoginPassword') }}</span>
        <div class="password-wrap">
          <input :type="showPwd ? 'text' : 'password'" v-model="password" required minlength="6" />
          <button type="button" class="eye" @click="showPwd = !showPwd" :title="showPwd ? t.hide : t.show">
            <span v-if="showPwd">🙈</span>
            <span v-else>👁</span>
          </button>
        </div>
      </label>

      <button type="submit" class="primary" :disabled="busy" v-tx="'LoginSubmit'">
        {{ busy ? t.submitting : tx('LoginSubmit') }}
      </button>
    </form>

    <div class="forgot">
      <router-link :to="{ name: 'forgot-password' }" v-tx="'LoginForgot'">{{ tx('LoginForgot') }}</router-link>
    </div>

    <div class="alt" v-if="showBack">
      <router-link :to="{ name: 'login' }">{{ t.back }}</router-link>
    </div>
  </div>
</template>

<script>
const I18N = {
  az: { submitting: "Daxil olunur…", show: "Göstər", hide: "Gizlət", back: "← Geri",
        failed: "Daxil olmaq alınmadı", welcome: "Xoş gəldiniz!", wrongRole: "Bu hesab fərqli rol üçündür" },
  de: { submitting: "Wird angemeldet…", show: "Anzeigen", hide: "Verbergen", back: "← Zurück",
        failed: "Anmeldung fehlgeschlagen", welcome: "Willkommen!", wrongRole: "Dieses Konto gehört einer anderen Rolle" },
  en: { submitting: "Signing in…", show: "Show", hide: "Hide", back: "← Back",
        failed: "Sign in failed", welcome: "Welcome!", wrongRole: "This account is for a different role" },
  ru: { submitting: "Вход…", show: "Показать", hide: "Скрыть", back: "← Назад",
        failed: "Не удалось войти", welcome: "Добро пожаловать!", wrongRole: "Этот аккаунт для другой роли" },
  tr: { submitting: "Giriş yapılıyor…", show: "Göster", hide: "Gizle", back: "← Geri",
        failed: "Giriş başarısız", welcome: "Hoş geldiniz!", wrongRole: "Bu hesap farklı bir rol içindir" },
};

// Admin tərəfindən redaktə edilə bilən etiketlər üçün fallback (resx/statics boş gəlsə istifadə olunur)
const LABEL_FALLBACK = {
  az: { LoginEmail: "Email", LoginPassword: "Şifrə", LoginSubmit: "Daxil ol", LoginForgot: "Şifrəni unutmusunuz?" },
  de: { LoginEmail: "E-Mail", LoginPassword: "Passwort", LoginSubmit: "Anmelden", LoginForgot: "Passwort vergessen?" },
  en: { LoginEmail: "Email", LoginPassword: "Password", LoginSubmit: "Sign in", LoginForgot: "Forgot password?" },
  ru: { LoginEmail: "Email", LoginPassword: "Пароль", LoginSubmit: "Войти", LoginForgot: "Забыли пароль?" },
  tr: { LoginEmail: "E-posta", LoginPassword: "Şifre", LoginSubmit: "Giriş yap", LoginForgot: "Şifremi unuttum?" },
};

export default {
  name: "LoginForm",
  props: {
    expectedRole: { type: String, default: null }, // null = bütün rollar qəbul olunur
    title: { type: String, required: true },
    subtitle: { type: String, required: true },
    showBack: { type: Boolean, default: false },
  },
  data() { return { email: "", password: "", busy: false, showPwd: false }; },
  computed: {
    t() { return I18N[this.$store.state.lang] || I18N.az; },
  },
  methods: {
    tx(key) {
      const v = this.$store.state.statics?.[key];
      if (v && String(v).trim()) return v;
      const lang = this.$store.state.lang || "az";
      return (LABEL_FALLBACK[lang] || LABEL_FALLBACK.az)[key] || "";
    },
    async onSubmit() {
      this.busy = true;
      try {
        const role = await this.$store.dispatch("auth/login", { email: this.email, password: this.password });
        if (!role) {
          this.$toast.error(this.t.failed);
          return;
        }
        if (this.expectedRole && role !== this.expectedRole) {
          await this.$store.dispatch("auth/logout");
          this.$toast.error(this.t.wrongRole);
          return;
        }
        this.$toast.success(this.t.welcome);
        const ret = this.$route.query.returnUrl;
        if (ret) { this.$router.push(ret); return; }
        if (role === "Clinic") this.$router.push({ name: "clinic-dashboard" });
        else if (role === "Nurse") this.$router.push({ name: "profile-nurse" });
        else this.$router.push({ name: "home" });
      } catch (e) {
        // toast shown by interceptor
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.auth-card {
  background: #fff; border-radius: 12px; padding: 2rem; box-shadow: 0 2px 14px rgba(0,0,0,0.06);
  h2 { color: #1e3c72; margin-bottom: 0.25rem; }
  .muted { color: #64748b; margin-bottom: 1.5rem; font-size: 0.9rem; }
  .field { display: flex; flex-direction: column; gap: 0.3rem; margin-bottom: 1rem;
    span { font-size: 0.78rem; color: #475569; font-weight: 600; }
    input { padding: 0.6rem 0.8rem; border: 1px solid #d0d5dd; border-radius: 6px; font: inherit; outline: none;
      &:focus { border-color: #2a5298; }
    }
  }
  .password-wrap { position: relative;
    input { width: 100%; padding-right: 2.6rem; box-sizing: border-box; }
    .eye { position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
      width: 30px; height: 30px; border: 0; background: transparent; cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center; border-radius: 5px;
      &:hover { background: #f1f5f9; }
    }
  }
  button.primary { width: 100%; padding: 0.7rem; background: #2a5298; color: #fff; border: 0; border-radius: 6px; cursor: pointer; font: inherit; font-weight: 600;
    &:hover:not(:disabled) { background: #1e3c72; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
  .forgot { margin-top: 0.85rem; text-align: center;
    a { font-size: 0.82rem; color: #2a5298; text-decoration: none; &:hover { text-decoration: underline; } }
  }
  .alt { margin-top: 1.25rem; padding-top: 1rem; border-top: 1px solid #eef0f4; text-align: center; font-size: 0.85rem;
    a { color: #2a5298; text-decoration: none; &:hover { text-decoration: underline; } }
  }
}
</style>
